/**
 * Faixas por cidade da tela Clássica (protótipo aprovado v4) — Nexora Grid (GUS-1503/1508).
 *
 * Criado em: 18/10/2026 · Última modificação: 18/10/2026 · Versão: 2.0.0
 *
 * - Cada cidade é uma faixa. À esquerda fica a **frente**: o card da cidade e a fila de atenção (setores com anomalia,
 *   o pior primeiro). A anomalia nunca sai da tela.
 * - A esteira dos setores normais corre por baixo da frente e some atrás dela, que é opaca e fica por cima. Ela pausa
 *   ao apontar ou focar e volta 2,5 s depois.
 * - Congelar para a esteira. O Movimento reduzido a transforma em rolagem manual, com uma cópia só.
 * - Um setor que entra na fila chega com a animação do protótipo (`chegou`).
 * v2.0 (GUS-1508): visual e comportamento do protótipo, sobre o modelo (`dados.ts`) e os cards (`Cards.tsx`).
 */
import { useEffect, useRef } from "react";
import { anomalo } from "./anomalia";
import { CardCidade, CardSetor } from "./Cards";
import type { Cidade, Modelo } from "./dados";

const RETOMAR_MS = 2500;

function Faixa({ c, i, modelo, congelado, reduzido }: { c: Cidade; i: number; modelo: Modelo; congelado: boolean; reduzido: boolean }) {
  const fila = c.setores.filter((s) => anomalo(s.nota));
  const normais = c.setores.filter((s) => !anomalo(s.nota));
  const esteira = useRef<HTMLDivElement>(null);
  const desloc = useRef(i * 70);
  const pausaAte = useRef(0);
  const naFila = useRef<Set<string> | null>(null);
  const filaEl = useRef<HTMLDivElement>(null);

  // card novo na fila de atenção: chega com destaque (não na primeira montagem)
  useEffect(() => {
    const agora = new Set(fila.map((s) => s.codigo));
    if (naFila.current && !reduzido) {
      agora.forEach((codigo) => {
        if (!naFila.current!.has(codigo)) filaEl.current?.querySelector(`[data-card="${codigo}"]`)?.classList.add("chegou");
      });
      if ([...agora].some((x) => !naFila.current!.has(x))) pausaAte.current = performance.now() + 5000;
    }
    naFila.current = agora;
  });

  useEffect(() => {
    if (reduzido) { if (esteira.current) esteira.current.style.transform = ""; return; }
    let antes = performance.now();
    const velocidade = 24 + i * 4;
    let quadro = requestAnimationFrame(function passo(agora) {
      const dt = Math.min(0.05, (agora - antes) / 1000);
      antes = agora;
      const el = esteira.current;
      if (el && !congelado && agora >= pausaAte.current) {
        const terco = el.scrollWidth / 3;
        desloc.current = (desloc.current + velocidade * dt) % (terco || 1);
        el.style.transform = `translateX(${-desloc.current}px)`;
      }
      quadro = requestAnimationFrame(passo);
    });
    return () => cancelAnimationFrame(quadro);
  }, [congelado, reduzido, i]);

  const pausar = () => { pausaAte.current = Infinity; };
  const soltar = () => { pausaAte.current = performance.now() + RETOMAR_MS; };
  const copias = reduzido ? 1 : 3;
  return (
    <div className="faixa" data-faixa={i} data-linha={c.municipio.codigo} aria-label={`Cidade ${c.municipio.nome}`}>
      <div className="frente">
        <CardCidade c={c} />
        <div className="fila" ref={filaEl} aria-label="Fila de atenção">
          {fila.length > 0 && <><span className="rotulo-fila" aria-hidden>atenção</span>{fila.map((s) => <CardSetor key={s.codigo} s={s} modelo={modelo} />)}</>}
        </div>
      </div>
      <div className={`trilho${reduzido ? " manual" : ""}`} onPointerOver={pausar} onPointerLeave={soltar} onFocus={pausar} onBlur={soltar}
        aria-label={`Setores normais de ${c.municipio.nome}`}>
        <div className="esteira" ref={esteira}>
          {Array.from({ length: copias }, (_, k) => normais.map((s) => <CardSetor key={`${k}-${s.codigo}`} s={s} modelo={modelo} copia={k > 0} />))}
        </div>
      </div>
    </div>
  );
}

export function Faixas({ modelo, congelado, reduzido }: { modelo: Modelo; congelado: boolean; reduzido: boolean }) {
  return (
    <div className="faixas">
      {modelo.cidades.map((c, i) => <Faixa key={c.municipio.codigo} c={c} i={i} modelo={modelo} congelado={congelado} reduzido={reduzido} />)}
      {!modelo.cidades.length && <p className="aviso">aguardando dados do tempo real…</p>}
    </div>
  );
}
